'use client'

import { useCallback, useSyncExternalStore } from 'react'

export type StorageArea = 'local' | 'session'

/**
 * Read a Web Storage key as React state.
 *
 * The pattern this replaces was: hold the value in `useState` seeded with a
 * default, read storage in a mount effect and `setState` the real value, and
 * write through on every change. It has three problems.
 *
 * The mount effect is a synchronous set-state inside an effect, which stops
 * the React Compiler optimising the component. Every component that reads the
 * same key keeps its own copy, so one component writing it leaves the others
 * showing a stale value until they happen to remount. And nothing notices a
 * write from another tab.
 *
 * `useSyncExternalStore` fixes all three: one store, one snapshot, every
 * reader re-rendered on a change from anywhere. The server snapshot is `null`
 * — storage genuinely is unreadable there — so SSR and hydration render the
 * caller's default and settle to the stored value immediately afterwards,
 * which is what the mount effect did, without the hydration mismatch that a
 * lazy `useState` initializer would cause.
 *
 * Values are raw strings. Callers own their own encoding; there is no JSON
 * layer here because a parse failure has no good generic answer.
 */

const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

/** `storage` fires for writes made by *other* tabs; same-tab writes notify directly. */
function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)
  window.addEventListener('storage', onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
    window.removeEventListener('storage', onStoreChange)
  }
}

function area(which: StorageArea): Storage | null {
  // Accessing the property itself throws in a sandboxed iframe, so the guard
  // has to be around the access and not just around the get/set.
  try {
    return which === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

export function readStored(which: StorageArea, key: string): string | null {
  try {
    return area(which)?.getItem(key) ?? null
  } catch {
    return null
  }
}

export function writeStored(which: StorageArea, key: string, value: string | null): void {
  try {
    const store = area(which)
    if (store) {
      if (value === null) store.removeItem(key)
      else store.setItem(key, value)
    }
  } catch {
    // Private mode or a restricted context: the value stays in memory for
    // this session's subscribers and is simply not persisted.
  }
  notify()
}

/**
 * The stored string for `key`, and a setter that persists and broadcasts.
 * Passing `null` to the setter removes the key.
 */
export function useStoredValue(
  which: StorageArea,
  key: string,
): [string | null, (value: string | null) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => readStored(which, key),
    () => null,
  )

  const set = useCallback(
    (next: string | null) => writeStored(which, key, next),
    [which, key],
  )

  return [value, set]
}
